import { Link, useParams } from 'react-router';

import { SESSION_CODE_LENGTH, SESSION_TTL_SECONDS, isSessionCode } from '@dimensionscore/shared';

import { useSchemaIndex, useSchemaSource } from '../schema/useSchema';

/**
 * The editor's front door: every config the generator knows about, as a list.
 *
 * The list is not written down anywhere in this file. It comes from the schema index, which
 * comes from the committed contract mirror (dev) or the sidecar's artifact route (prod), so a
 * config POJO added six months from now appears here the moment its schema is generated -
 * no route added, no entry added, no component written. That is the same promise the block
 * registry keeps for fields, applied to navigation.
 *
 * Two routes land here on purpose. `/editor` is the nav entry, reached by a human who has not
 * opened a session yet. `/editor/:code` is the URL the sidecar actually mints and the mod
 * actually prints in chat - it must render something that acknowledges the code, because the
 * first thing anyone will ever do with this page is click that link, and a page that ignored
 * the code would be indistinguishable from the wrong page.
 */
export function EditorPage(): React.JSX.Element {
  const { code } = useParams<{ code: string }>();
  const minutes = Math.round(SESSION_TTL_SECONDS / 60);

  return (
    <section className="dc-card" data-testid="page-editor" {...(code === undefined ? {} : { 'data-session-code': code })}>
      <h1>Config editor</h1>

      {code === undefined ? (
        <p className="dc-dim">
          Pick a config below to see its form. Landing an edit on the server still needs a session: run <code>/cdim editor</code> in game and open the
          link it prints.
        </p>
      ) : isSessionCode(code) ? (
        <p className="dc-dim" data-testid="editor-session-code">
          Session <code>{code}</code> is open. Redeem it in game with <code>/cdim apply {code}</code> before it expires — {minutes} minutes after it
          was opened, unless this deployment shortened that.
        </p>
      ) : (
        <p className="dc-dim" data-testid="editor-bad-code">
          That is not a session code. Codes are {SESSION_CODE_LENGTH} characters, printed by <code>/cdim editor</code>; retype it from the chat line
          rather than from memory.
        </p>
      )}

      <SchemaList code={isSessionCode(code) ? code : undefined} />

      {/*
       * THE SIXTH SURFACE, AND THE ONLY ONE THE LIST ABOVE CANNOT CONTAIN. `menus` is a
       * registered `EditableContent` exactly like warps, kits and rewards - but its documents
       * are FabricMenus' JSON, no config POJO of ours describes them, and the registration
       * says so with `NO_SCHEMA`. So `:schema-gen` emits nothing for it and the schema index
       * has no entry to list. It is named here instead, beside the list, because an admin
       * looking for "where do I edit menus" must find it in the same place they find
       * everything else - and because a surface that is editable but unreachable is worse than
       * one that is not editable at all.
       */}
      <p className="dc-menus-entry">
        <Link to={code === undefined ? '/editor/menus' : `/editor/menus?session=${code}`} data-testid="menus-link">
          Menus
        </Link>{' '}
        <span className="dc-dim">
          — the FabricMenus layouts under <code>config/FabricMenus/Menus/</code>. Not in the list above because they have no generated schema: the
          format is another mod’s, so the documents in an open session are the list.
        </span>
      </p>
    </section>
  );
}

/**
 * The list, and the session it must not drop.
 *
 * THE CODE TRAVELS ON EVERY LINK. `SchemaFormPage` binds a session from `?session=<code>` and
 * from nowhere else - it is the only way that page can know which server's bundle to fetch -
 * so a link that omitted it would silently unbind the session an admin had just been handed
 * by `/cdim editor`: no capture tray, no reference catalog, no bundle, and no error either,
 * because "no session" is a legitimate state reached from the plain `/editor` nav entry. The
 * menus link beside this list has always carried it; these did not, which made the main path
 * through the editor the one path that lost the session.
 */
function SchemaList({ code }: { code: string | undefined }): React.JSX.Element {
  const source = useSchemaSource();
  const index = useSchemaIndex(source);
  const query = code === undefined ? '' : `?session=${code}`;

  if (index.state === 'loading') {
    return (
      <p className="dc-dim" data-testid="schema-list-loading">
        Loading the list of configs…
      </p>
    );
  }

  if (index.state === 'error') {
    return (
      <p className="dc-error" data-testid="schema-list-error">
        {index.message}
      </p>
    );
  }

  if (index.value.schemas.length === 0) {
    return (
      <p className="dc-dim" data-testid="schema-list-empty">
        No schemas are being served. On a deployment that means the sidecar’s <code>ARTIFACT_DIR</code> has no <code>schema/</code> directory — copy
        it from the server as the runbook’s artifact step describes. In a dev checkout, run <code>./gradlew :schema-gen:generate</code> and reload.
      </p>
    );
  }

  return (
    <ul className="dc-results dc-schema-list" data-testid="schema-list" data-schema-source={index.value.source}>
      {index.value.schemas.map((schema) => (
        <li key={schema.id} data-schema-id={schema.id}>
          <Link to={`/editor/schema/${schema.id}${query}`}>
            <span className="dc-result-name">{schema.title}</span>
            <span className="dc-result-uuid">
              <code>{schema.id}.json</code>
            </span>
            <span className="dc-result-seen">mod {schema.modVersion}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
